import React, { FunctionComponent, useMemo, useEffect, useState } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useParams } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import moment from 'moment';
import classNames from 'classnames';
import { DealerService } from '../../../../services/DealerService';
import { DealerStepSummary } from '../../../DealerStepSummary/DealerStepSummary';
import { ErrorMessage } from '../../../ErrorMessage/ErrorMessage';
import { Spinner } from '../../../Spinner/Spinner';
import { getStepCompletePercent } from '../../../DealerLaunchDashboard/steps';
import { LinkButton } from '../../../Input/Button/LinkButton';
import { ACTION_ITEMS } from '../../actionItems';
import { Button } from '../../../Input/Button/Button';
import { Dealer } from '../../../../types/dealerTypes';
import styles from './styles.module.scss';
import { CarService } from '../../../../services/CarService';
import { LaunchPadService } from '../../../../services/LaunchPadService';

export const SupportViewDealer: FunctionComponent = () => {
  const { dealerId } = useParams<{ dealerId: string }>();
  const dealerRequest = useAsync(DealerService.getDealer, [dealerId]);
  const updateDealer = useAsyncCallback(DealerService.updateDealer);
  const dealer = dealerRequest.result?.data;

  const [hasCars, setHasCars] = useState<boolean>();
  const [
    sendPricingValidationEmailModalOpen,
    setSendPricingValidationEmailModalOpen,
  ] = useState(false);
  // calculate the progress for all steps completed
  const stepCompletePercent = useMemo(
    () => (dealer ? getStepCompletePercent(dealer) : 0),
    [dealer],
  );

  useEffect(() => {
    // Determine if inventory was already set up properly by trying to get a sample car
    async function findCars() {
      if (dealer?.Feeds?.length) {
        const result = await CarService.getSampleCar(dealer.id);
        setHasCars(!!result.car);
      } else {
        setHasCars(false);
      }
    }
    findCars();
  }, [dealer]);

  const actionItems = dealer
    ? ACTION_ITEMS.filter((item) => item.isActive(dealer, hasCars))
    : [];

  const saveDealer = async (
    dealerUpdate: Partial<Dealer>,
    successMessage: string,
  ) => {
    if (!dealer) return;

    await updateDealer
      .execute({
        dealerId: dealer.id,
        dealerUpdate,
        mergeObjectFields: true,
      })
      .then((data) => {
        dealerRequest.set({
          status: 'success',
          loading: false,
          error: undefined,
          result: data,
        });
        toast(successMessage);
      });
  };

  const sendPricingValidation = async () => {
    await LaunchPadService.sendPricingValidationEmail(dealerId);
    saveDealer(
      {
        launchpad: {
          status: { implementerVerifiedInventory: true },
        },
      },
      'Pricing validation email has been sent successfully',
    );
    setSendPricingValidationEmailModalOpen(false);
  };

  return (
    <>
      <Modal
        className={styles.sendPricingValidationEmailModal}
        isOpen={sendPricingValidationEmailModalOpen}
        onRequestClose={() => setSendPricingValidationEmailModalOpen(false)}
      >
        <div className={styles.modalTitle}>Send Pricing Validation</div>
        <div className={styles.modalSubTitle}>
          Are you sure that you would like to send the pricing validation link
          for this customer?
        </div>
        <div className={styles.sendPricingValidationEmailModalButtonsContainer}>
          <Button
            className={styles.sendPricingValidationEmailModalButton}
            label="Cancel"
            onClick={() => setSendPricingValidationEmailModalOpen(false)}
          />
          <Button
            className={styles.sendPricingValidationEmailModalButton}
            label="Yes, Send Email"
            variant="primary"
            onClick={() => sendPricingValidation()}
          />
        </div>
      </Modal>
      <div className={styles.container}>
        {(dealerRequest.loading ||
          updateDealer.loading ||
          hasCars === undefined) && <Spinner variant="fullscreen" />}
        <ErrorMessage error={dealerRequest.error || updateDealer.error} />
        <div className={styles.header}>
          <div className={styles.dealerName}>
            {dealer?.name ? `${dealer.name}'s Setup` : null}
          </div>
          <div className="spacer" />
          <div className={styles.dealerContact}>
            <b>Email:</b> {dealer?.launchpad?.email || 'N/A'}
          </div>
          <div>
            <b>First Login:</b>{' '}
            {dealer?.launchpad?.firstLogin
              ? moment(dealer?.launchpad?.firstLogin).format('MM/DD/YYYY')
              : 'N/A'}
          </div>
        </div>
        <div className={styles.pageGrid}>
          {dealer ? (
            <>
              <div className={classNames(styles.card, styles.sectionSummary)}>
                <DealerStepSummary dealer={dealer} useChairmanLinks />
              </div>
              <div className={classNames(styles.card, styles.progressSummary)}>
                <h1>Progress Summary</h1>
                <div className={styles.progressTitle}>
                  Total Progress
                  <div className="spacer" />
                  <span className={styles.progressPercent}>
                    {(stepCompletePercent * 100).toFixed(0)}% Complete
                  </span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressBarFill}
                    style={{ width: `${stepCompletePercent * 100}%` }}
                  />
                </div>
                <br />
                <div>
                  Dealer Submission Status:{' '}
                  {dealer?.launchpad?.status?.readyToLaunch ? (
                    <>
                      Submitted (Dealer is locked out)
                      <br />
                      <br />
                      <Button
                        label="Unlock LaunchPad for Dealer"
                        variant="primary"
                        onClick={() =>
                          saveDealer(
                            {
                              launchpad: { status: { readyToLaunch: false } },
                            },
                            'Dealer access has been unlocked successfully',
                          )
                        }
                      />
                    </>
                  ) : (
                    'Not Submitted'
                  )}
                  {dealer?.launchpad?.status?.readyToBeginInventoryConfig &&
                    hasCars && (
                      <>
                        <br />
                        <br />
                        {dealer.launchpad?.email ? (
                          <Button
                            label="Send Pricing Validation Email"
                            onClick={() =>
                              setSendPricingValidationEmailModalOpen(true)
                            }
                          />
                        ) : (
                          <div>
                            This dealer was created in launchpad without an
                            email, so you cannot send them a pricing validation
                            email.
                          </div>
                        )}
                      </>
                    )}
                </div>
              </div>
              <div className={classNames(styles.card, styles.actionItems)}>
                <h1>Actions To Be Taken</h1>
                {actionItems.length ? (
                  actionItems.map((actionItem, index) => {
                    if (actionItem.getChairmanUrl) {
                      return (
                        <LinkButton
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className={styles.actionItemButton}
                          label={actionItem.name}
                          to={actionItem.getChairmanUrl(dealer)}
                          variant="minimal"
                          rawLink
                          openNewWindow
                        />
                      );
                    }
                    return (
                      <div className={styles.actionItemButton}>
                        {actionItem.name}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.actionItemsEmpty}>
                    No outstanding action items
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};
